import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  GraduationCap,
  Calendar,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Plus,
  FileText,
  Euro,
  BookOpen,
  Target
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch admin stats
      const statsRes = await fetch(`${API}/stats/admin`, {
        credentials: 'include'
      });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      // Fetch recent courses as sessions
      const coursesRes = await fetch(`${API}/courses/`, {
        credentials: 'include'
      });
      if (coursesRes.ok) {
        const courses = await coursesRes.json();
        setRecentSessions(courses.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickStats = [
    {
      label: 'Sessions en cours',
      value: stats?.published_courses || 0,
      icon: GraduationCap,
      color: 'bg-blue-500',
      trend: '+12%'
    },
    {
      label: 'Apprenants actifs',
      value: stats?.total_students || 0,
      icon: Users,
      color: 'bg-emerald-500',
      trend: '+8%'
    },
    {
      label: 'Inscriptions ce mois',
      value: stats?.total_enrollments || 0,
      icon: TrendingUp,
      color: 'bg-teal-500',
      trend: '+15%'
    },
    {
      label: 'Certificats délivrés',
      value: stats?.total_certificates || 0,
      icon: CheckCircle,
      color: 'bg-purple-500',
      trend: '+5%'
    }
  ];

  const upcomingTasks = [
    { id: 1, title: 'Valider les inscriptions en attente', type: 'urgent', count: 3 },
    { id: 2, title: 'Finaliser le programme "Leadership avancé"', type: 'normal', count: null },
    { id: 3, title: 'Envoyer les convocations de session', type: 'normal', count: 12 },
    { id: 4, title: 'Préparer le bilan pédagogique', type: 'deadline', count: null },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">
              Bonjour, {user?.name?.split(' ')[0]} 👋
            </h1>
            <p className="text-slate-600 mt-1">
              Voici un aperçu de votre activité de formation
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => navigate('/sessions/new')}
              data-testid="new-session-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle session
            </Button>
            <Button 
              className="bg-teal-500 hover:bg-teal-600 text-white"
              onClick={() => navigate('/commercial/inscriptions')}
              data-testid="inscriptions-btn"
            >
              <Users className="h-4 w-4 mr-2" />
              Inscriptions
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickStats.map((stat, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {stat.trend} ce mois
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center shadow-lg`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Sessions récentes */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-lg">Sessions de formation récentes</CardTitle>
                  <CardDescription>Vos dernières sessions actives</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/sessions')}>
                  Voir tout
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : recentSessions.length === 0 ? (
                  <div className="text-center py-8">
                    <GraduationCap className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">Aucune session active</p>
                    <Button 
                      variant="outline" 
                      className="mt-3"
                      onClick={() => navigate('/sessions/new')}
                    >
                      Créer une session
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentSessions.map((session) => (
                      <div
                        key={session.course_id}
                        className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-all cursor-pointer"
                        onClick={() => navigate(`/sessions/${session.course_id}`)}
                      >
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-100 to-teal-50 flex items-center justify-center">
                          <BookOpen className="h-6 w-6 text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-900 truncate">{session.title}</h4>
                          <p className="text-sm text-slate-500">{session.category} • {session.duration_hours}h</p>
                        </div>
                        <Badge variant={session.is_published ? 'default' : 'secondary'} className="shrink-0">
                          {session.is_published ? 'Active' : 'Brouillon'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tâches et alertes */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Tâches à faire</CardTitle>
                <CardDescription>Actions en attente</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {upcomingTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <div className={cn(
                        "w-2 h-2 rounded-full mt-2 shrink-0",
                        task.type === 'urgent' ? 'bg-red-500' :
                        task.type === 'deadline' ? 'bg-teal-500' : 'bg-slate-300'
                      )} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700">{task.title}</p>
                        {task.count && (
                          <p className="text-xs text-slate-500">{task.count} élément(s)</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Agenda du jour</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Session Leadership</p>
                      <p className="text-xs text-slate-500">9h00 - 12h00 • Salle A</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-teal-50 border border-teal-100">
                    <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
                      <Target className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Réunion pédagogique</p>
                      <p className="text-xs text-slate-500">14h00 - 15h30 • Visio</p>
                    </div>
                  </div>
                </div>
                <Button variant="ghost" className="w-full mt-3 text-slate-600" onClick={() => navigate('/agenda')}>
                  <Calendar className="h-4 w-4 mr-2" />
                  Voir l'agenda complet
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Indicateurs Qualiopi */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Indicateurs Qualiopi</CardTitle>
                <CardDescription>Suivi de vos critères qualité</CardDescription>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                <CheckCircle className="h-3 w-3 mr-1" />
                Certifié
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { label: 'Évaluations complétées', value: 92, target: 100 },
                { label: 'Taux de satisfaction', value: 88, target: 85 },
                { label: 'Documents à jour', value: 95, target: 100 },
                { label: 'Suivi post-formation', value: 78, target: 80 },
              ].map((indicator, index) => (
                <div key={index} className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">{indicator.label}</span>
                    <span className={cn(
                      "text-sm font-bold",
                      indicator.value >= indicator.target ? 'text-emerald-600' : 'text-teal-600'
                    )}>
                      {indicator.value}%
                    </span>
                  </div>
                  <Progress 
                    value={indicator.value} 
                    className="h-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">Objectif: {indicator.target}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// Helper function
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default Dashboard;
